import {
	Autocomplete,
	Card,
	CardContent,
	Collapse,
	Grid2,
	TextField,
} from "@mui/material";
import { Controller, type Control } from "react-hook-form";
import { ProductSelector } from "@/pages/workOrders/components/ProductSelector";
import {
	ClipSpecsPanel,
	useClipSpecs,
} from "@/pages/workOrders/components/ClipSpecsPanel";
import {
	ProductSpecsPanel,
	SectionTitle,
} from "@/pages/workOrders/components/ProductSpecsPanel";
import type {
	AssetType,
	AssetTypeConfig,
} from "@/pages/workOrders/config/assetFieldConfig";
import type { Clip } from "@/shared/models/Clip";
import type { ProductForSelector } from "@/shared/models/WorkOrder";
import type { GroupedDelayAsset } from "@/shared/repositories/DelayAssetRepository";
import { ClipSelector } from "@/shared/selectors/ClipSelector";
import { NextLocationSelector } from "@/shared/selectors/NextLocationSelector";
import type { EventForm } from "./EventEditorModal";

type FieldsProps = {
	control: Control<EventForm>;
	assets: GroupedDelayAsset[];
	isWoEdit: boolean;
	isQuantityLocked: boolean;
	isClip: boolean;
	areaAssetId: number;
	extendedPartNumber: string;
	setSelectedProduct: (value: ProductForSelector | null) => void;
	setSelectedClip: (value: Clip | null) => void;
};
export const WorkOrderEventFields = ({
	control,
	assets,
	isWoEdit,
	isQuantityLocked,
	isClip,
	areaAssetId,
	extendedPartNumber,
	setSelectedProduct,
	setSelectedClip,
}: FieldsProps) => (
	<>
		<Grid2 size={{ xs: 12, md: 4 }}>
			<Controller
				name="AssetId"
				control={control}
				rules={{ required: "Line is required" }}
				render={({ field, fieldState: { error } }) => (
					<Autocomplete
						size="small"
						disabled={isWoEdit}
						options={assets}
						getOptionLabel={(o) => o.AssetName}
						value={assets.find((a) => a.AssetId === field.value) ?? null}
						isOptionEqualToValue={(o, v) => o.AssetId === v.AssetId}
						onChange={(_, v) => {
							field.onChange(v?.AssetId ?? null);
							setSelectedProduct(null);
							setSelectedClip(null);
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Line"
								error={!!error}
								helperText={error?.message}
							/>
						)}
					/>
				)}
			/>
		</Grid2>
		<Grid2 size={{ xs: 12, md: 5 }}>
			{isClip ? (
				<Controller
					name="ClipId"
					control={control}
					rules={{ required: "Clip is required" }}
					render={({ field, fieldState: { error } }) => (
						<ClipSelector
							label="Clip"
							value={field.value}
							onChange={(clipId, clip) => {
								field.onChange(clipId);
								setSelectedClip(clip);
							}}
							fieldError={error}
							disabled={isWoEdit}
						/>
					)}
				/>
			) : (
				<Controller
					name="ProductId"
					control={control}
					rules={{ required: "Product is required" }}
					render={({ field, fieldState: { error } }) => (
						<ProductSelector
							label="Part #"
							value={field.value}
							onChange={field.onChange}
							onProductSelect={setSelectedProduct}
							fieldError={error}
							areaAssetId={areaAssetId}
							disabled={isWoEdit}
						/>
					)}
				/>
			)}
		</Grid2>
		<Grid2 size={{ xs: 6, md: 3 }}>
			<Controller
				name="Quantity"
				control={control}
				rules={{
					required: "Quantity is required",
					min: { value: 1, message: "Must be > 0" },
				}}
				render={({ field, fieldState: { error } }) => (
					<TextField
						label="Quantity (pcs)"
						type="number"
						value={field.value ?? ""}
						onChange={(e) =>
							field.onChange(e.target.value ? Number(e.target.value) : null)
						}
						error={!!error}
						helperText={error?.message}
						size="small"
						fullWidth
						disabled={isQuantityLocked}
					/>
				)}
			/>
		</Grid2>
		<Grid2 size={{ xs: 12, md: 6 }}>
			<TextField
				label="Extended Part #"
				value={extendedPartNumber}
				size="small"
				fullWidth
				disabled
			/>
		</Grid2>
		<Grid2 size={{ xs: 12, md: 6 }}>
			<Controller
				name="Notes"
				control={control}
				render={({ field }) => (
					<TextField
						label="Notes"
						value={field.value}
						onChange={field.onChange}
						size="small"
						fullWidth
						slotProps={{ htmlInput: { maxLength: 500 } }}
					/>
				)}
			/>
		</Grid2>
	</>
);

type DetailsProps = {
	control: Control<EventForm>;
	config: AssetTypeConfig;
	showDetails: boolean;
	isClip: boolean;
	clipSpecs: ReturnType<typeof useClipSpecs>;
	clipIdValue: number | null;
	productData: ProductForSelector | null;
	assetType: AssetType;
	quantity: number | null;
};
export const WorkOrderEventDetails = ({
	control,
	config,
	showDetails,
	isClip,
	clipSpecs,
	clipIdValue,
	productData,
	assetType,
	quantity,
}: DetailsProps) => (
	<Collapse in={showDetails} unmountOnExit>
		{config.showNextLocation && (
			<Card sx={{ mb: 2 }}>
				<CardContent>
					<SectionTitle mb={1}>Line Configuration</SectionTitle>

					<Grid2 container spacing={2} alignItems="center">
						{config.showNextLocation && (
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Controller
									name="NextLocations"
									control={control}
									render={({ field }) => (
										<NextLocationSelector
											dense
											value={field.value}
											onChange={field.onChange}
										/>
									)}
								/>
							</Grid2>
						)}
					</Grid2>
				</CardContent>
			</Card>
		)}

		{isClip ? (
			<ClipSpecsPanel specs={clipSpecs} clipId={clipIdValue} />
		) : (
			<ProductSpecsPanel
				product={productData}
				config={config}
				assetType={assetType}
				quantity={quantity ?? null}
				columns={3}
				sawColumns={2.4}
				showBundleTotals
				fieldColumns={{
					CustomerName: 4,
					SectionName: 4,
					GradeName: 4,
					BottomTolerance: 4,
					TargetLength: 4,
					TopTolerance: 4,
				}}
			/>
		)}
	</Collapse>
);
